import { Component, DestroyRef, OnInit, TemplateRef, inject } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { UsersService } from 'src/app/services/users/users.service';
import { UserInterface, UsersListResponse } from 'src/app/services/users/interface/user.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { mergeMap, tap } from 'rxjs';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  host: {
    class: 'flex flex-col flex-1 min-h-0 overflow-x-hidden'
  }
})

export class UserManagementComponent implements OnInit {

  activeDropdownId: number | null = null;
  users: UserInterface[] = [];
  selectedUser: UserInterface | null = null;
  itemsPerPage = 20;
  currentPage = 1;
  displayedUsers: UserInterface[] = [];
  isListLoading: boolean = false;
  userModalRef?: BsModalRef;
  confirmDeleteModalRef?: BsModalRef;
  resetPasswordModalRef?: BsModalRef;
  userId: number;
  userToEdit: UserInterface | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(private modalService: BsModalService,
              private usersService: UsersService) {}

  ngOnInit() {
    this.getUsers();
  }

  toggleDropdown(id: number, event: Event) {
    event.stopPropagation();
    if (this.activeDropdownId === id) {
      this.activeDropdownId = null;
    } else {
      this.activeDropdownId = id;
    }
  }

  getUsers() {
    this.isListLoading = true;

    this.usersService.getUsers().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res: UsersListResponse) => {
        this.users = res.data;
        this.displayedUsers = this.getUsersForPage(this.currentPage);
        this.isListLoading = false;
      },
      error: () => {
        this.isListLoading = false;
      }
    })
  }

  openNewUserModal(template: TemplateRef<any>) {
    this.userModalRef = this.modalService.show(
      template,
      Object.assign({}, { class: 'new-user-modal', ignoreBackdropClick: true, keyboard: false })
    );
  }

  openConfirmDeleteModal(template: TemplateRef<any>, userId: number) {
    this.userId = userId;
    this.confirmDeleteModalRef = this.modalService.show(template, Object.assign({}, { class: 'confirm-delete-modal' }));
  }

  openResetPasswordModal(template: TemplateRef<any>, user: UserInterface) {
    this.selectedUser = user;
    this.resetPasswordModalRef = this.modalService.show(template, Object.assign({}, { class: 'reset-password-modal' }));
  }

  onDeleteUser(userId: number) {
    this.isListLoading = true;

    this.usersService.deleteUser(userId).pipe(
      tap(() => this.confirmDeleteModalRef?.hide()),
      mergeMap(() => this.usersService.getUsers()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res: UsersListResponse) => {
        this.users = res.data;
        this.displayedUsers = this.getUsersForPage(this.currentPage);
        this.isListLoading = false;
      },
      error: () => {
        this.isListLoading = false;
      }
    })

    this.displayedUsers = this.getUsersForPage(this.currentPage);
    if (this.displayedUsers.length === 0 && this.currentPage > 1) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  getUsersForPage(page: number): any[] {
    const startIndex = (page - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.users.slice(startIndex, endIndex);
  }

  onPageChange(page: number): void {
    if (page >= 1) {
      this.currentPage = page;
      this.displayedUsers = this.getUsersForPage(page);
      this.scrollListToTop();
    }
  }

  getPages(): number[] {
    const totalPages = Math.ceil(this.users.length / this.itemsPerPage);
    return Array(totalPages).fill(0).map((_, index) => index + 1);
  }

  onUserUpdatedOrCreated() {
    this.userToEdit = null;
    this.getUsers();
  }

  userRoleName(user_role_id: number): string {
    switch (user_role_id) {
      case 1:
        return 'Super Admin';
      case 2:
        return 'Admin';
      case 3:
        return 'User';
      case 4:
        return 'Design consultant';
      default:
        return '';
    }
  }

  openEditUserModal(user: UserInterface, template: TemplateRef<any>) {
    this.userToEdit = user;
    this.userModalRef = this.modalService.show(
      template,
      Object.assign({}, { class: 'new-user-modal', ignoreBackdropClick: true, keyboard: false })
    );
  }

  onUserRowClick(user: UserInterface, template: TemplateRef<any>): void {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    this.openEditUserModal(user, template);
  }

  pageChanged(event: any): void {
    const nextPage = Number(event?.page);
    if (!nextPage || nextPage < 1 || nextPage === this.currentPage || this.isListLoading) {
      return;
    }

    this.currentPage = nextPage;
    this.displayedUsers = this.getUsersForPage(this.currentPage);
    this.scrollListToTop();
  }

  private scrollListToTop(): void {
    const tableWrapper = document.querySelector('.users-table-wrapper') as HTMLElement | null;
    tableWrapper?.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const appMain = document.querySelector('main') as HTMLElement | null;
    appMain?.scrollTo({ top: 0, behavior: 'auto' });

    const scrollingElement = document.scrollingElement as HTMLElement | null;
    scrollingElement?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }
}
